import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { QuestionsAndAnswersItemContent } from './QuestionsAndAnswersItem/QuestionsAndAnswersItemContent'
import { useModal } from '../../../../hooks/useModal'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { CreateUpdateQuestionsAndAnswersSectionModal } from './CreateUpdateQuestionsAndAnswersSectionModal'
// eslint-disable-next-line max-len
import { CreateUpdateQuestionsAndAnswersItemModal } from './QuestionsAndAnswersItem/CreateUpdateQuestionsAndAnswersItemModal'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { AccordionSection } from '../../../common/AccordionSection'
import { Option } from '../../../common/Dropdown'
import { deleteFaqSection } from '../../../../api/faqs'
import { FaqSectionModel } from '../../../../state/stores/faqs'

interface QuestionsAndAnswersSectionProps {
  section: FaqSectionModel
}

export const QuestionsAndAnswersSection = ({
  section,
}: QuestionsAndAnswersSectionProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const {
    show: showCreateQuestionModal,
    open: openCreateQuestionModal,
    close: closeCreateQuestionModal,
  } = useModal()
  const {
    show: showEditSectionModal,
    open: openEditSectionModal,
    close: closeEditSectionModal,
  } = useModal()
  const {
    show: showRemoveSectionConfirmationModal,
    open: openRemoveSectionConfirmationModal,
    close: closeRemoveSectionConfirmationModal,
  } = useModal()
  const authUserId = useAuthenticatedUserId()

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  const accordionQuestions =
    section.questions?.map((question) => ({
      ...question,
      content: question.answer,
      title: question.question,
    })) || []

  const questionIds = section.questions?.map((question) => question._id) ?? []

  const { mutate: mutateDeleteFaq } = useMutation({
    mutationFn: deleteFaqSection,

    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          'createUpdateQuestionsAndAnswers.flashMessage.deleteSectionSuccess'
        )
      )
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate(
          'createUpdateQuestionsAndAnswers.flashMessage.deleteSectionFailure'
        )
      )
      captureException(error)
    },
  })
  const removeSection = async (): Promise<void> => {
    mutateDeleteFaq({ id: section._id, questionIds })
  }

  const dropdownOptions: Option[] | undefined = isAdmin
    ? [
        {
          value: 'create',
          label: translate(
            'createUpdateQuestionsAndAnswers.addNewQuestionTitle'
          ),
          onClick: () => openCreateQuestionModal(),
        },
        {
          value: 'edit',
          label: translate('common.actions.edit'),
          onClick: () => openEditSectionModal(),
        },
        {
          value: 'remove',
          label: translate('common.actions.delete'),
          onClick: () => openRemoveSectionConfirmationModal(),
          destructive: true,
        },
      ]
    : undefined

  return (
    <div className="mb-10 w-full space-y-10">
      <AccordionSection
        title={section.section}
        items={accordionQuestions.map((_question) => ({
          id: _question._id,
          title: `${_question.title}`,
          content: (
            <QuestionsAndAnswersItemContent
              sectionId={section._id}
              questionId={_question._id}
              content={_question.content}
            />
          ),
        }))}
        dropdownOptions={dropdownOptions}
        itemsEmptyText={translate(
          'questionsAndAnswersView.emptyState.noQuestionsAndAnswersText'
        )}
      />
      <CreateUpdateQuestionsAndAnswersItemModal
        sectionId={section._id}
        show={showCreateQuestionModal}
        close={closeCreateQuestionModal}
      />
      <CreateUpdateQuestionsAndAnswersSectionModal
        sectionId={section._id}
        questionIds={questionIds}
        show={showEditSectionModal}
        close={closeEditSectionModal}
      />
      <ConfirmationModal
        title={translate(
          'createUpdateQuestionsAndAnswers.removeSectionConfirmationModal.title'
        )}
        description={translate(
          'createUpdateQuestionsAndAnswers.removeSectionConfirmationModal.description'
        )}
        show={showRemoveSectionConfirmationModal}
        close={closeRemoveSectionConfirmationModal}
        onConfirm={removeSection}
        deleteMode
      />
    </div>
  )
}
